import type { UpdateRecord } from '../types/book'

export type HistoryDomain = [number, number]

export type HistoryChartPoint = {
  x: number
  index: number
  timestamp: number
  progress: number
  location?: string
  progressPercent: number
  isRecent: boolean
}

const LOG_GAP_UNIT_MS = 60 * 1000
const MIN_COMPRESSED_GAP_MS = 30 * 1000
const MIN_VIEWPORT_RATIO = 0.03
const RECENT_POINT_COUNT = 6
const DEFAULT_HISTORY_VIEWPORT_POINT_COUNT = 10
const MAX_HISTORY_CHART_POINT_COUNT = 50
const HISTORY_PROGRESS_DOMAIN_MIN_PADDING = 0.5
const HISTORY_PROGRESS_DOMAIN_MIN_SPAN = 2
const HISTORY_PROGRESS_DOMAIN_PADDING_RATIO = 0.12

const sortUpdates = (updates: UpdateRecord[]): UpdateRecord[] =>
  [...updates].sort((a, b) => a[0] - b[0])

const domainSpan = (domain: HistoryDomain): number =>
  Math.max(domain[1] - domain[0], 1)

const clampProgressPercent = (value: number): number =>
  Math.min(100, Math.max(0, value))

const roundHistoryProgressBoundary = (
  value: number,
  direction: 'up' | 'down',
): number => {
  const scaledValue = value * 10
  return (
    direction === 'down' ? Math.floor(scaledValue) : Math.ceil(scaledValue)
  ) / 10
}

const getCompressedHistoryGap = (gapMs: number): number =>
  Math.max(
    MIN_COMPRESSED_GAP_MS,
    Math.log1p(Math.max(0, gapMs) / LOG_GAP_UNIT_MS) * LOG_GAP_UNIT_MS,
  )

const findBackCandidate = (
  updates: UpdateRecord[],
  lastUpdate: UpdateRecord,
  minSeconds = 60,
  minProgress = 0.001,
): UpdateRecord | null => {
  const [lastTimestamp, lastProgress] = lastUpdate

  for (let index = updates.length - 1; index >= 0; index--) {
    const update = updates[index]
    if (
      update[0] < lastTimestamp - minSeconds * 1000 &&
      Math.abs(update[1] - lastProgress) > minProgress
    ) {
      return update
    }
  }

  return null
}

export const buildCompressedHistoryChartPoints = (
  updates: UpdateRecord[],
): HistoryChartPoint[] => {
  const sorted = sortUpdates(updates)
  let x = 0

  return sorted.map(([timestamp, progress, location], index) => {
    if (index > 0) {
      const gapMs = Math.max(0, timestamp - sorted[index - 1][0])
      x += getCompressedHistoryGap(gapMs)
    }

    return {
      x,
      index,
      timestamp,
      progress,
      location,
      progressPercent: Math.round(progress * 1000) / 10,
      isRecent: index >= sorted.length - RECENT_POINT_COUNT,
    }
  })
}

export const getCompressedHistoryXForTimestamp = (
  points: HistoryChartPoint[],
  timestamp: number,
): number | null => {
  if (!Number.isFinite(timestamp) || points.length === 0) {
    return null
  }

  const firstPoint = points[0]
  if (timestamp <= firstPoint.timestamp) {
    return firstPoint.x
  }

  for (let index = 1; index < points.length; index++) {
    const previousPoint = points[index - 1]
    const nextPoint = points[index]
    if (timestamp > nextPoint.timestamp) {
      continue
    }

    const timestampSpan = nextPoint.timestamp - previousPoint.timestamp
    if (timestampSpan <= 0) {
      return nextPoint.x
    }

    const progressThroughGap = Math.min(
      Math.max((timestamp - previousPoint.timestamp) / timestampSpan, 0),
      1,
    )
    return previousPoint.x + (nextPoint.x - previousPoint.x) * progressThroughGap
  }

  const lastPoint = points[points.length - 1]
  return lastPoint.x + getCompressedHistoryGap(timestamp - lastPoint.timestamp)
}

export const getHistoryDomainBounds = (
  points: HistoryChartPoint[],
): HistoryDomain => {
  if (points.length === 0) {
    return [0, 1]
  }

  if (points.length === 1) {
    return [points[0].x, points[0].x + MIN_COMPRESSED_GAP_MS]
  }

  return [points[0].x, points[points.length - 1].x]
}

export const getMinimumHistoryViewportSpan = (
  points: HistoryChartPoint[],
): number => {
  if (points.length < 2) {
    return 1
  }

  let minimumGap = Number.POSITIVE_INFINITY
  for (let index = 1; index < points.length; index++) {
    minimumGap = Math.min(minimumGap, Math.max(points[index].x - points[index - 1].x, 1))
  }

  return Math.max(minimumGap * 3, domainSpan(getHistoryDomainBounds(points)) * MIN_VIEWPORT_RATIO)
}

export const getDefaultHistoryViewport = (
  points: HistoryChartPoint[],
  bounds: HistoryDomain,
  minimumSpan: number,
  visiblePointCount = DEFAULT_HISTORY_VIEWPORT_POINT_COUNT,
): HistoryDomain | null => {
  if (points.length === 0 || points.length <= visiblePointCount) {
    return null
  }

  const startPoint = points[Math.max(points.length - visiblePointCount, 0)]
  return clampHistoryDomain([startPoint.x, bounds[1]], bounds, minimumSpan)
}

export const clampHistoryDomain = (
  domain: HistoryDomain,
  bounds: HistoryDomain,
  minimumSpan: number,
): HistoryDomain => {
  const fullSpan = domainSpan(bounds)
  const span = Math.min(Math.max(domainSpan(domain), minimumSpan), fullSpan)

  if (span >= fullSpan) {
    return [bounds[0], bounds[1]]
  }

  let start = domain[0]
  let end = start + span

  if (start < bounds[0]) {
    start = bounds[0]
    end = start + span
  }

  if (end > bounds[1]) {
    end = bounds[1]
    start = end - span
  }

  return [start, end]
}

export const zoomHistoryDomain = (
  domain: HistoryDomain,
  bounds: HistoryDomain,
  factor: number,
  center: number,
  minimumSpan: number,
): HistoryDomain => {
  if (!Number.isFinite(factor) || factor <= 0) {
    return clampHistoryDomain(domain, bounds, minimumSpan)
  }

  const span = domainSpan(domain)
  const nextSpan = span * factor
  const relativeCenter =
    span <= 0 ? 0.5 : Math.min(Math.max((center - domain[0]) / span, 0), 1)

  return clampHistoryDomain(
    [
      center - nextSpan * relativeCenter,
      center + nextSpan * (1 - relativeCenter),
    ],
    bounds,
    minimumSpan,
  )
}

export const panHistoryDomain = (
  domain: HistoryDomain,
  bounds: HistoryDomain,
  delta: number,
  minimumSpan: number,
): HistoryDomain => {
  if (!Number.isFinite(delta) || delta === 0) {
    return clampHistoryDomain(domain, bounds, minimumSpan)
  }

  return clampHistoryDomain(
    [domain[0] + delta, domain[1] + delta],
    bounds,
    minimumSpan,
  )
}

export const getVisibleHistoryPoints = (
  points: HistoryChartPoint[],
  domain: HistoryDomain,
): HistoryChartPoint[] =>
  points.filter((point) => point.x >= domain[0] && point.x <= domain[1])

export const getRenderableHistoryPoints = (
  points: HistoryChartPoint[],
  domain: HistoryDomain,
  maxPointCount = MAX_HISTORY_CHART_POINT_COUNT,
): HistoryChartPoint[] => {
  if (maxPointCount <= 0) {
    return []
  }

  if (points.length <= 2) {
    return points
  }

  const visiblePoints = getVisibleHistoryPoints(points, domain)
  if (visiblePoints.length <= maxPointCount) {
    return visiblePoints
  }

  const firstVisiblePoint = visiblePoints[0]
  const lastVisiblePoint = visiblePoints[visiblePoints.length - 1]
  const visibleSpan = Math.max(lastVisiblePoint.x - firstVisiblePoint.x, 1)
  const sampledPoints: HistoryChartPoint[] = [firstVisiblePoint]
  let cursor = 1

  for (let sampleIndex = 1; sampleIndex < maxPointCount - 1; sampleIndex++) {
    const targetX =
      firstVisiblePoint.x +
      (sampleIndex * visibleSpan) / Math.max(maxPointCount - 1, 1)

    while (
      cursor < visiblePoints.length - 1 &&
      visiblePoints[cursor].x < targetX
    ) {
      cursor++
    }

    const previousPoint = visiblePoints[Math.max(cursor - 1, 0)]
    const nextPoint = visiblePoints[Math.min(cursor, visiblePoints.length - 1)]
    const closestPoint =
      Math.abs(nextPoint.x - targetX) < Math.abs(targetX - previousPoint.x)
        ? nextPoint
        : previousPoint
    const lastSampledPoint = sampledPoints[sampledPoints.length - 1]

    if (closestPoint.index !== lastSampledPoint.index) {
      sampledPoints.push(closestPoint)
    }
  }

  if (
    sampledPoints[sampledPoints.length - 1].index !== lastVisiblePoint.index
  ) {
    sampledPoints.push(lastVisiblePoint)
  }

  return sampledPoints
}

export const getHistoryProgressDomain = (
  points: HistoryChartPoint[],
  domain: HistoryDomain,
): [number, number] => {
  const visiblePoints = getVisibleHistoryPoints(points, domain)
  const sourcePoints = visiblePoints.length > 0 ? visiblePoints : points
  if (sourcePoints.length === 0) {
    return [0, 100]
  }

  const minProgressPercent = Math.min(
    ...sourcePoints.map((point) => point.progressPercent),
  )
  const maxProgressPercent = Math.max(
    ...sourcePoints.map((point) => point.progressPercent),
  )
  const progressSpan = maxProgressPercent - minProgressPercent
  const padding =
    progressSpan > 0
      ? Math.max(
          HISTORY_PROGRESS_DOMAIN_MIN_PADDING,
          progressSpan * HISTORY_PROGRESS_DOMAIN_PADDING_RATIO,
        )
      : 1

  let domainStart = roundHistoryProgressBoundary(
    clampProgressPercent(minProgressPercent - padding),
    'down',
  )
  let domainEnd = roundHistoryProgressBoundary(
    clampProgressPercent(maxProgressPercent + padding),
    'up',
  )

  if (domainEnd - domainStart >= HISTORY_PROGRESS_DOMAIN_MIN_SPAN) {
    return [domainStart, domainEnd]
  }

  const center = (minProgressPercent + maxProgressPercent) / 2
  const halfSpan = HISTORY_PROGRESS_DOMAIN_MIN_SPAN / 2

  domainStart = roundHistoryProgressBoundary(
    clampProgressPercent(center - halfSpan),
    'down',
  )
  domainEnd = roundHistoryProgressBoundary(
    clampProgressPercent(center + halfSpan),
    'up',
  )

  if (domainEnd - domainStart >= HISTORY_PROGRESS_DOMAIN_MIN_SPAN) {
    return [domainStart, domainEnd]
  }

  if (domainStart <= 0) {
    return [0, HISTORY_PROGRESS_DOMAIN_MIN_SPAN]
  }

  if (domainEnd >= 100) {
    return [100 - HISTORY_PROGRESS_DOMAIN_MIN_SPAN, 100]
  }

  return [
    roundHistoryProgressBoundary(center - halfSpan, 'down'),
    roundHistoryProgressBoundary(center + halfSpan, 'up'),
  ]
}

export const getHistoryTickValues = (
  points: HistoryChartPoint[],
  domain: HistoryDomain,
  tickCount = 5,
): number[] => {
  const visiblePoints = getVisibleHistoryPoints(points, domain)
  if (visiblePoints.length === 0) {
    return []
  }

  if (visiblePoints.length <= tickCount) {
    return visiblePoints.map((point) => point.x)
  }

  const values = new Set<number>()
  for (let tickIndex = 0; tickIndex < tickCount; tickIndex++) {
    const pointIndex = Math.round(
      (tickIndex * (visiblePoints.length - 1)) / Math.max(tickCount - 1, 1),
    )
    values.add(visiblePoints[pointIndex].x)
  }

  return [...values]
}

export const getInlineHistoryLabelIndices = (
  points: HistoryChartPoint[],
  domain: HistoryDomain,
  maxVisibleCount = 8,
): number[] => {
  const visiblePoints = getVisibleHistoryPoints(points, domain)
  if (visiblePoints.length === 0 || visiblePoints.length > maxVisibleCount) {
    return []
  }

  return visiblePoints.map((point) => point.index)
}

export const resolveHistoryBackTarget = (
  updates: UpdateRecord[],
  currentUpdate: UpdateRecord = [Date.now(), -1],
  minSeconds = 60,
  minProgress = 0.001,
): UpdateRecord | null => {
  const sortedUpdates = sortUpdates(updates)
  if (sortedUpdates.length === 0) {
    return null
  }

  if (currentUpdate[1] === -1) {
    return (
      findBackCandidate(sortedUpdates, currentUpdate, minSeconds, minProgress) ||
      sortedUpdates[sortedUpdates.length - 1]
    )
  }

  let probe = currentUpdate
  const startingProgress = currentUpdate[1]
  const seen = new Set<string>()

  while (true) {
    const candidate = findBackCandidate(
      sortedUpdates,
      probe,
      minSeconds,
      minProgress,
    )

    if (!candidate) {
      return null
    }

    if (Math.abs(candidate[1] - startingProgress) > minProgress) {
      return candidate
    }

    const candidateKey = `${candidate[0]}:${candidate[1]}`
    if (seen.has(candidateKey)) {
      return null
    }
    seen.add(candidateKey)
    probe = candidate
  }
}

export const resolveHistoryForwardTarget = (
  updates: UpdateRecord[],
  currentUpdate: UpdateRecord = [0, -1],
  minSeconds = 60,
  minProgress = 0.001,
): UpdateRecord | null => {
  const sortedUpdates = sortUpdates(updates)
  if (sortedUpdates.length === 0) {
    return null
  }

  const [lastTimestamp, lastProgress] = currentUpdate
  for (const update of sortedUpdates) {
    if (
      update[0] > lastTimestamp + minSeconds * 1000 &&
      Math.abs(update[1] - lastProgress) > minProgress
    ) {
      return update
    }
  }

  return null
}
